import {get, post} from 'sr/utils/axios/index'
import { getShowInternalAccounts } from 'sr/utils/helpers/internalAccounts'
import {transformPayloadToFilter} from '../helpers/processFilter'
import {JmmApiResponse, Status} from './globalInterface'

export interface CompanyRegion {
  regionId?: string
  city: string
  state: string
  display: string
}

export interface CompanyResponse {
  username: string
  first_name?: string
  last_name?: string
  email: string
  mobile_number: string
  company_name: string
  business_type: string[]
  intent: string[]
  candidate_msg: boolean
  isAllowedPreOnBordingSubmission?: boolean
  requiredPreOnBordingChecklist?: {
    name?: string
    isRequired?: boolean
  }[]
  isAllowedOnBordingSubmission?: boolean
  requiredOnBordingChecklist?: {
    name?: string
    isRequired?: boolean
    isDocumentRequired?: boolean
  }[]
  no_of_clients?: number
  user_id: string
  status: Status
  isInternal?: boolean
  isEmailVerified?:boolean,
  isPhoneVerified?:boolean
  createdAt: string
  updatedAt: string
  id: string
  loginCount?: number
  lastLogin?: string
  // Region data from branches/locations
  primaryRegion?: string | null
  regionCount?: number
  regions?: CompanyRegion[]
  address?: any
}

export interface CompanyStats {
  totalLogins: number
  totalLocations: number
  uniqueRegions: number
}

export interface FetchCompanyResponse extends Omit<JmmApiResponse<CompanyResponse[]>, 'stats'> {
  stats?: CompanyStats
}

export type FetchSingleCompanyResponse = JmmApiResponse<CompanyResponse>

export interface CompanyFilters {
  limit?: number
  page?: number
  sortBy?: string
  sort?: string
  projectBy?: string
  business_type?: string
  intent?: string
  candidate_msg?: boolean
  status?: Status
  includeInternal?: boolean
  'createdAt[$gte]'?: string
  'createdAt[$lte]'?: string
  // Partial name search fields (supports regex matching)
  name?: string // Searches company_name
  company_name?: string
  username?: string
  email?: string
  mobile_number?: string
  jobPosted?: number
  'jobPosted[$gt]'?: number
}

export const fetchCompany = async (payload: Record<string, any>): Promise<FetchCompanyResponse> => {
  const includeInternal = payload.includeInternal ?? getShowInternalAccounts()
  const filteredPayload = transformPayloadToFilter({ ...payload, includeInternal })

  try {
    const res = await get<FetchCompanyResponse>(`/company`, filteredPayload)

    if (res.success === true && res.data) {
      return res // Return the fetched data
    } else {
      throw new Error('No data found')
    }
  } catch (error) {
    throw new Error(`Failed to fetch: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}
export const fetchSingleCompany = async (id: string): Promise<FetchSingleCompanyResponse> => {
  try {
    const res = await get<FetchSingleCompanyResponse>(`/company`, {id})

    if (res.success === true && res.data) {
      return res // Return the fetched data
    } else {
      throw new Error('No data found')
    }
  } catch (error) {
    throw new Error(`Failed to fetch: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

export interface DeriveLocationResponse {
  success: boolean
  msg: string
  data?: {
    company: {
      id: string
      company_name: string
      mobile_number: string
    }
    derivedLocation: {
      areaCode: string
      stateCode: string
      stateName: string
      city: string | null
      regionId: string | null
    }
    branch: any
  }
  existingState?: string
  existingRegion?: string
}

/**
 * Derive location from company phone number area code
 * This is a manual admin action to populate location for companies without branch data
 */
export const deriveLocationFromPhone = async (companyId: string): Promise<DeriveLocationResponse> => {
  try {
    const res = await post<DeriveLocationResponse>(`/company/derive-location`, { companyId })
    return res
  } catch (error: any) {
    // Return the error response from the API if available
    if (error?.response?.data) {
      return error.response.data
    }
    throw new Error(`Failed to derive location: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}
